import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  CheckCircle,
  WarningCircle,
  UploadSimple,
  Trash,
  Plug,
  ArrowsClockwise,
  Key,
} from "@phosphor-icons/react";

export default function PainelSession() {
  const [info, setInfo] = useState(null);
  const [token, setToken] = useState("");
  const [packageId, setPackageId] = useState(62);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/painel/session");
      setInfo(data);
      if (data?.package_id) setPackageId(data.package_id);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async () => {
    const t = token.trim();
    if (!t) return toast.error("Cole o Bearer token");
    setBusy(true);
    try {
      const { data } = await api.post("/painel/session", {
        token: t,
        package_id: Number(packageId) || 62,
      });
      setInfo(data);
      setToken("");
      toast.success("Token salvo com sucesso");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!confirm("Remover o token salvo?")) return;
    await api.delete("/painel/session");
    setTestResult(null);
    load();
    toast.success("Token removido");
  };

  const testConn = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/painel/test-connection");
      setTestResult(data);
      if (data.ok) toast.success("Token válido — API respondendo");
      else toast.error(`Falha (HTTP ${data.status})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no teste");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout
      title="Sessão painel.fun"
      subtitle="API / Bearer Token"
      action={
        <button
          data-testid="reload-session-btn"
          onClick={load}
          className="flex items-center gap-2 border border-[#E5E7EB] px-4 py-2 text-sm hover:bg-[#F8F9FA] transition-colors duration-150"
        >
          <ArrowsClockwise size={16} weight="bold" /> Atualizar
        </button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-[#E5E7EB] bg-white p-6">
          <div className="label-mono">Status</div>
          <h2 className="font-display font-bold text-lg mt-1 mb-5 tracking-tight">
            Token salvo
          </h2>
          {info ? (
            <div className="space-y-2 text-sm">
              <Row label="Modo" value={info.mode} ok={info.mode === "live"} />
              <Row label="Configurado" value={info.configured ? "Sim" : "Não"} ok={info.configured} />
              <Row label="API base" value={info.api_base} />
              <Row label="Token (preview)" value={info.token_preview || "—"} />
              <Row label="Pacote (trial)" value={info.package_id ?? 62} />
              <Row
                label="Salvo em"
                value={info.saved_at ? new Date(info.saved_at).toLocaleString("pt-BR") : "—"}
              />
            </div>
          ) : (
            <div className="text-sm text-[#64748B]">Carregando…</div>
          )}

          {info?.configured && (
            <div className="mt-5 flex flex-col gap-2">
              <button
                data-testid="test-conn-btn"
                onClick={testConn}
                disabled={busy}
                className="flex items-center justify-center gap-2 bg-[#002FA7] text-white text-sm py-2 hover:bg-[#001f7a] transition-colors duration-150 disabled:opacity-50"
              >
                <Plug size={16} weight="bold" /> Testar token
              </button>
              <button
                data-testid="clear-session-btn"
                onClick={clear}
                className="flex items-center justify-center gap-2 border border-[#EF4444] text-[#EF4444] text-sm py-2 hover:bg-[#EF4444] hover:text-white transition-colors duration-150"
              >
                <Trash size={16} weight="bold" /> Remover token
              </button>
            </div>
          )}

          {testResult && (
            <div
              data-testid="test-result"
              className={`mt-4 p-3 border text-sm ${
                testResult.ok
                  ? "border-[#10B981]/40 bg-[#10B981]/5"
                  : "border-[#F59E0B]/40 bg-[#F59E0B]/5"
              }`}
            >
              {testResult.ok ? (
                <CheckCircle size={18} weight="duotone" className="text-[#10B981] inline mr-1" />
              ) : (
                <WarningCircle size={18} weight="duotone" className="text-[#F59E0B] inline mr-1" />
              )}
              {testResult.ok ? "API respondendo OK" : `Falha HTTP ${testResult.status}`}
              {testResult.body_preview && (
                <div className="text-xs text-[#64748B] mt-1 mono break-all">
                  {testResult.body_preview}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 border border-[#E5E7EB] bg-white p-6">
          <div className="label-mono">Configurar</div>
          <h2 className="font-display font-bold text-lg mt-1 mb-3 tracking-tight flex items-center gap-2">
            <Key size={20} weight="duotone" /> Bearer token
          </h2>
          <p className="text-sm text-[#64748B] leading-relaxed mb-4">
            Cole abaixo o token JWT que o painel.fun guarda no seu navegador após login.
            Veja a seção <em>Como pegar o token</em> abaixo. Ele tem o formato{" "}
            <span className="kbd">eyJhbGci…</span> e fica válido por horas/dias.
          </p>
          <textarea
            data-testid="token-input"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            rows={6}
            placeholder="eyJhbGciOiJIUzI1NiIs..."
            className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-xs rounded-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          />
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <label className="label-mono block mb-2">Package ID (trial)</label>
              <input
                data-testid="package-id-input"
                type="number"
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
              <p className="text-xs text-[#64748B] mt-1">Default 62 (P2P trial)</p>
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <button
                data-testid="upload-token-btn"
                onClick={upload}
                disabled={busy || !token.trim()}
                className="flex items-center gap-2 bg-[#002FA7] text-white text-sm px-5 py-2 hover:bg-[#001f7a] transition-colors duration-150 disabled:opacity-50"
              >
                <UploadSimple size={16} weight="bold" /> {busy ? "Salvando..." : "Salvar token"}
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 border border-[#E5E7EB] bg-[#F8F9FA] p-6">
          <div className="label-mono">Como pegar o token</div>
          <h2 className="font-display font-bold text-lg mt-1 mb-4 tracking-tight">
            Passo-a-passo (~ 30 segundos)
          </h2>
          <ol className="text-sm text-[#0F172A] space-y-3 list-decimal pl-5 leading-relaxed max-w-3xl">
            <li>
              Abra <span className="kbd">https://painel.fun</span> em uma aba do Chrome
              e faça login normalmente (resolve o CAPTCHA como humano).
            </li>
            <li>
              Após logado, aperte <span className="kbd">F12</span> para abrir o DevTools
              e clique na aba <strong>Network</strong>.
            </li>
            <li>
              No painel, clique em qualquer link/menu (ex: <em>Gerenciar Usuário</em>).
              Você verá várias requisições para <span className="kbd">api.controle.fit</span>
              aparecerem na lista.
            </li>
            <li>
              Clique em qualquer uma delas (a primeira já serve). Na coluna da direita,
              vá em <strong>Headers</strong> → role até <strong>Request Headers</strong>.
            </li>
            <li>
              Procure por <span className="kbd">authorization: Bearer eyJ…</span> e
              <strong> copie TUDO depois de "Bearer "</strong> (o JWT começa com{" "}
              <span className="kbd">eyJ</span>).
            </li>
            <li>
              Cole no campo acima e clique em <em>Salvar token</em>. Depois clique em{" "}
              <em>Testar token</em> — se ficar verde, está pronto.
            </li>
            <li>
              No <span className="kbd">backend/.env</span>, mude{" "}
              <span className="kbd">PAINEL_MODE=live</span> e reinicie o backend.
              A partir daí cada teste vai chamar a API real do painel.fun.
            </li>
          </ol>

          <div className="mt-5 p-3 border border-[#002FA7]/30 bg-[#002FA7]/5 text-xs text-[#0F172A] max-w-3xl">
            <strong>💡 Por que esse caminho?</strong> O painel.fun usa Cloudflare Turnstile
            só na hora do <em>login</em>. Depois que você está logado, todas as ações
            usam um JWT que o navegador armazena. Reusando esse JWT a gente pula
            o captcha sem precisar de browser automation ou serviços pagos.
            <br /><br />
            <strong>⏰ Renovação:</strong> o token JWT geralmente expira em 12-24h.
            Quando o teste falhar com "Token inválido", basta refazer o passo-a-passo
            (leva 30 segundos).
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#E5E7EB] last:border-0">
      <div className="label-mono">{label}</div>
      <div
        className={`text-sm mono truncate max-w-[60%] text-right ${
          ok === undefined ? "text-[#0F172A]" : ok ? "text-[#10B981]" : "text-[#F59E0B]"
        }`}
      >
        {String(value)}
      </div>
    </div>
  );
}
